/**
 * Production-safe logging utility
 *
 * All logs are automatically stripped in production builds to ensure
 * no console statements exist in the production bundle.
 *
 * @example
 * ```ts
 * import { logger } from '@/lib/utils/logger';
 *
 * logger.debug('User action:', action);
 * logger.info('API response:', data);
 * logger.warn('Deprecated feature used');
 * logger.error('Critical error:', error);
 * ```
 */

const isDev = import.meta.env.DEV;

export const logger = {
  /**
   * Debug-level logging (development only)
   * Use for detailed debugging information
   */
  debug: (...args: unknown[]): void => {
    if (isDev) {
      console.debug('[DEBUG]', ...args);
    }
  },

  /**
   * Info-level logging (development only)
   * Use for general informational messages
   */
  info: (...args: unknown[]): void => {
    if (isDev) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Warning-level logging (development only)
   * Use for potentially problematic situations
   */
  warn: (...args: unknown[]): void => {
    if (isDev) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Error-level logging (development only)
   * Use for error conditions that need attention
   */
  error: (...args: unknown[]): void => {
    if (isDev) {
      console.error('[ERROR]', ...args);
    }
  },
};
